import { CatalogsQuery } from 'src/ts/@types/generated/graphql/catalog-service/graphql';
import logger from 'logger';
import { Tags } from 'src/ts/enums/tags.enums';
import { FunnelCartDelivery } from 'src/ts/types/cart';
import { DateTime } from 'luxon';

export interface CustomSorter<
  Additional extends Record<string, unknown> = Record<string, unknown>,
> {
  (args: {
    catalogItemA: CatalogsQuery['catalogs'][0]['items'][0];
    catalogItemB: CatalogsQuery['catalogs'][0]['items'][0];
    additional: Additional;
  }): number | undefined;
}

export type Sorter = {
  displayName?: string;
  sorters: CatalogItemSorter[];
  additional?: any;
};

type CatalogItemSorter<
  Additional extends Record<string, unknown> = Record<string, unknown>,
> = {
  customSorter?: CustomSorter<Additional>;
  defaultSorter?: {
    direction: 'asc' | 'desc';
    propertyAccessor: (
      item: CatalogsQuery['catalogs'][0]['items'][0],
    ) => string | number | undefined;
  };
  additional?: Additional;
};

export const sortCatalogItems = (input: {
  catalogItems: CatalogsQuery['catalogs'][0]['items'];
  catalogSorters: CatalogItemSorter<any>[];
}): CatalogsQuery['catalogs'][0]['items'] => {
  const { catalogItems, catalogSorters } = input;

  return [...catalogItems].sort((catalogItemA, catalogItemB) => {
    for (const { defaultSorter, customSorter, additional } of catalogSorters) {
      if (defaultSorter) {
        const evalDefaultSorter = defaultSorterFn({
          catalogItemA,
          catalogItemB,
          catalogSorter: defaultSorter,
        });

        if (evalDefaultSorter !== undefined) {
          return evalDefaultSorter;
        }
      }

      if (customSorter) {
        const evalCustomSorter = customSorter({
          catalogItemA,
          catalogItemB,
          additional: additional || {},
        });

        if (evalCustomSorter !== undefined) {
          return evalCustomSorter;
        }
      }
    }

    return 0;
  });
};

const defaultSorterFn = (input: {
  catalogItemA: CatalogsQuery['catalogs'][0]['items'][0];
  catalogItemB: CatalogsQuery['catalogs'][0]['items'][0];
  catalogSorter: CatalogItemSorter['defaultSorter'];
}): number | undefined => {
  const { propertyAccessor, direction } = input.catalogSorter!;
  const { catalogItemA, catalogItemB } = input;

  const aValue = propertyAccessor(catalogItemA);
  const bValue = propertyAccessor(catalogItemB);

  if (aValue === undefined) {
    return direction === 'asc' ? 1 : -1;
  }

  if (bValue === undefined) {
    return direction === 'asc' ? -1 : 1;
  }

  if (aValue < bValue) return direction === 'asc' ? -1 : 1;
  if (aValue > bValue) return direction === 'asc' ? 1 : -1;
};

//
// Custom Sorters
//

export const cartDeliveryItemSorter: CustomSorter = ({
  catalogItemA,
  catalogItemB,
  additional: { cartDelivery },
}): number | undefined => {
  if (!cartDelivery) {
    return undefined;
  }

  const cartDeliveryVariantIds = (
    cartDelivery as FunnelCartDelivery
  ).lineItems.map((lineItem) => lineItem.variantId);

  // If you have nothing in your cart delivery, nothing to sort
  if (cartDeliveryVariantIds.length === 0) {
    return undefined;
  }

  const aVariantId = catalogItemA?.variant.id;
  const bVariantlId = catalogItemB?.variant.id;

  const aInCart = aVariantId && cartDeliveryVariantIds.includes(aVariantId);
  const bInCart = bVariantlId && cartDeliveryVariantIds.includes(bVariantlId);

  // First, sort by whether the item is in the cart
  if (aInCart && !bInCart) return -1;
  if (!aInCart && bInCart) return 1;

  // If both are in the cart, sort by their order in the cartDeliveryVariantIds array
  if (aInCart && bInCart) {
    return (
      cartDeliveryVariantIds.indexOf(aVariantId) -
      cartDeliveryVariantIds.indexOf(bVariantlId)
    );
  }

  return undefined;
};

/**
 * Featured meals first by provided ids
 */
export const featuredMealSorter: CustomSorter<{
  featuredSkuGroups: string[][];
}> = ({
  catalogItemA,
  catalogItemB,
  additional: { featuredSkuGroups },
}): number | undefined => {
  // This is being defensive in case someone doesn't provide this value.
  if (!featuredSkuGroups) {
    logger.error({
      msg: 'CantSortByFeaturedMealsMissingIds',
      extra: {
        featuredSkuGroups,
      },
    });

    return;
  }

  if (!featuredSkuGroups?.length) {
    // No-op
    return 0;
  }

  const aSku = catalogItemA?.variant.product.sku?.sku;
  const bSku = catalogItemB?.variant.product.sku?.sku;

  const aIsFeatured =
    aSku && featuredSkuGroups.some((skuGroup) => skuGroup.includes(aSku));
  const bIsFeatured =
    bSku && featuredSkuGroups.some((skuGroup) => skuGroup.includes(bSku));

  // First, sort by whether the item is featured
  if (aIsFeatured && !bIsFeatured) {
    return -1;
  }
  if (!aIsFeatured && bIsFeatured) {
    return 1;
  }

  // If both are featured, sort by their order in the array
  if (aIsFeatured && bIsFeatured) {
    const aSkuGroupIndex = featuredSkuGroups.findIndex((skuGroup) =>
      skuGroup.includes(aSku),
    );
    const bSkuGroupIndex = featuredSkuGroups.findIndex((skuGroup) =>
      skuGroup.includes(bSku),
    );
    return aSkuGroupIndex - bSkuGroupIndex;
  }

  // If neither is featured, maintain their relative order
  return 0;
};

/**
 * Put a la carte entries at end of the list
 */
export const aLaCarteTypeSorter: CustomSorter = ({
  catalogItemA,
  catalogItemB,
}): number | undefined => {
  const aIsALaCarte = catalogItemA?.variant.tags.some(
    (t) => t?.id === Tags.A_LA_CARTE,
  );
  const bIsALaCarte = catalogItemB?.variant.tags.some(
    (t) => t?.id === Tags.A_LA_CARTE,
  );

  // Ensure "A La Carte" items are moved to the end
  if (aIsALaCarte && !bIsALaCarte) return 1;
  if (!aIsALaCarte && bIsALaCarte) return -1;

  return undefined; // Maintain relative order if both or neither are "A La Carte"
};

/**
 * Sort by provided tag id
 */
export const tagSorter: CustomSorter = ({
  catalogItemA,
  catalogItemB,
  additional: { tagId, direction = 'beginning' },
}): number | undefined => {
  if (!tagId) {
    logger.error({
      msg: 'CantSortByTagMissingId',
      extra: {
        tagId,
      },
    });

    return;
  }

  const aHasTag = catalogItemA?.variant.tags.some((t) => t?.id === tagId);
  const bHasTag = catalogItemB?.variant.tags.some((t) => t?.id === tagId);

  // Ensure items with the specified tag are prioritized according to direction
  if (aHasTag && !bHasTag) {
    return direction === 'beginning' ? -1 : 1;
  }
  if (!aHasTag && bHasTag) {
    return direction === 'beginning' ? 1 : -1;
  }

  // Maintain relative order if both or neither have the tag
  return undefined;
};

//
// Default Sorter Property Accessors
//

export const recommenderScorePropertyAccessor = (
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0],
): number | undefined => {
  return catalogItem?.recommenderScore ?? undefined;
};

export const totalCarbsInGPropertyAccessor = (
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0],
): number | undefined => {
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return undefined;
  }

  return catalogItem.variant.macroNutrients.totalCarbsInG;
};

export const caloriesPropertyAccessor = (
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0],
): number | undefined => {
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return undefined;
  }

  return catalogItem.variant.macroNutrients.calories;
};

export const priceInCentsPropertyAccessor = (
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0],
): number | undefined => {
  return catalogItem?.priceInCents;
};

export const proteinInGPropertyAccessor = (
  catalogItem: CatalogsQuery['catalogs'][0]['items'][0],
): number | undefined => {
  if (catalogItem?.variant.__typename !== 'MealVariant') {
    return undefined;
  }

  return catalogItem.variant.macroNutrients.proteinInG;
};

export const sortCatalogItemsByDefault = (input: {
  catalogs: CatalogsQuery['catalogs'];
  cart?: {
    cartDeliveries: {
      deliveryDate: Date;
      lineItems: { variantId: number; quantity: number }[];
    }[];
  };
  featuredSkuGroups?: string[][];
}): CatalogsQuery['catalogs'] => {
  const { catalogs, cart, featuredSkuGroups } = input;

  // Before setting the final catalogs, this page needs to default sort by:
  // 1. What items are in the cart (if any)
  // 2. Featured garden meals
  // 3. A La Carte desc (ALC at the end of the list)
  // 4. By rank
  return catalogs.map((catalog) => {
    const cartDelivery = cart?.cartDeliveries.find(
      (cartDelivery) =>
        DateTime.fromJSDate(cartDelivery.deliveryDate, {
          zone: 'utc',
        }).toISODate() ===
        DateTime.fromJSDate(catalog.eligibleDeliveryDates[0], {
          zone: 'utc',
        }).toISODate(),
    );

    return {
      ...catalog,
      items: sortCatalogItems({
        catalogItems: catalog.items,
        catalogSorters: [
          {
            customSorter: cartDeliveryItemSorter,
            additional: {
              cartDelivery,
            },
          },
          {
            customSorter: featuredMealSorter,
            additional: {
              featuredSkuGroups: featuredSkuGroups || [],
            },
          },
          {
            customSorter: tagSorter,
            additional: { tagId: Tags.A_LA_CARTE, direction: 'end' },
          },
          {
            defaultSorter: {
              propertyAccessor: recommenderScorePropertyAccessor,
              direction: 'desc',
            },
          },
        ],
      }),
    };
  });
};
